import React from 'react';
import clsx from 'clsx';
import { motion, Variants } from 'framer-motion';
import { KeyboardArrowDown } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ActionEntireDay } from '../../../assets/chat/action_entire_day.svg';
import { ReactComponent as ActionWeek } from '../../../assets/chat/action_week.svg';
import type { SchedulerScope } from '../../../hooks/schedulerChat/types';
import AddPreferenceChip from '../../AiAssistantChat/bubbles/AddPreferenceChip';

const heroVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.15 + index * 0.12, duration: 0.35, ease: 'easeOut' as const },
  }),
};

interface SchedulerHeroSelectionProps {
  heroLocked: boolean;
  scheduleLabel: string;
  showDatePickerControl?: boolean;
  disableSelectionControls: boolean;
  taskEntriesLength: number;
  routineEntriesLength: number;
  selectedTaskCount: number;
  selectedRoutineCount: number;
  onOpenDatePicker: (event: React.MouseEvent<HTMLElement>) => void;
  onOpenTaskModal: (event: React.MouseEvent<HTMLElement>) => void;
  onOpenRoutineModal: (event: React.MouseEvent<HTMLElement>) => void;
  customRuleInput?: string;
  onCustomRuleInputChange?: (value: string) => void;
  isDatePickerOpen?: boolean;
  isTaskModalOpen?: boolean;
  isRoutineModalOpen?: boolean;
  schedulerScope?: SchedulerScope;
}

const SchedulerHeroSelection: React.FC<SchedulerHeroSelectionProps> = ({
  heroLocked,
  scheduleLabel,
  showDatePickerControl = true,
  disableSelectionControls,
  taskEntriesLength,
  routineEntriesLength,
  selectedTaskCount,
  selectedRoutineCount,
  onOpenDatePicker,
  onOpenTaskModal,
  onOpenRoutineModal,
  customRuleInput = '',
  onCustomRuleInputChange,
  isDatePickerOpen = false,
  isTaskModalOpen = false,
  isRoutineModalOpen = false,
  schedulerScope = 'day',
}) => {
  const { t } = useTranslation();
  const isWeekScope = schedulerScope === 'week';
  const PlannerIcon = isWeekScope ? ActionWeek : ActionEntireDay;
  const plannerLabel = isWeekScope
    ? t('task.scheduler.hero.weekPlanLabel', { defaultValue: 'Week Plan' })
    : t('planMyDayIntro.aiPlannerLabel', { defaultValue: 'AI Planner' });

  return (
    <div
      className={clsx('scheduler-chat-ui__hero', {
        'scheduler-chat-ui__hero--locked': heroLocked,
      })}
    >
      <motion.div
        className="scheduler-chat-ui__selection__container"
        variants={heroVariants}
        initial="hidden"
        animate="visible"
        custom={0}
      >
        <div className="scheduler-chat-ui__selection__planner-label">
          <PlannerIcon
            className="scheduler-chat-ui__selection__planner-icon"
            style={{ color: '#C9B8FF' }}
          />
          <span>{plannerLabel}</span>
        </div>
        <div className="scheduler-chat-ui__selection__sentence">
          <span className="scheduler-chat-ui__selection__text">
            {t('task.scheduler.hero.iWantToSchedule')}
          </span>
          <button
            type="button"
            className={clsx('scheduler-chat-ui__selection__pill', {
              'scheduler-chat-ui__selection__pill--active': isTaskModalOpen || selectedTaskCount > 0,
            })}
            onClick={onOpenTaskModal}
            disabled={disableSelectionControls || !taskEntriesLength}
          >
            {selectedTaskCount > 0 && (
              <span className="scheduler-chat-ui__selection__pill__badge">{selectedTaskCount}</span>
            )}
            <span className="scheduler-chat-ui__selection__pill__label">
              {t('task.scheduler.hero.tasks')}
            </span>
            <KeyboardArrowDown className="scheduler-chat-ui__selection__pill__icon" />
          </button>
          <span className="scheduler-chat-ui__selection__text">
            {t('task.scheduler.hero.and')}
          </span>
          <button
            type="button"
            className={clsx('scheduler-chat-ui__selection__pill', {
              'scheduler-chat-ui__selection__pill--active':
                isRoutineModalOpen || selectedRoutineCount > 0,
            })}
            onClick={onOpenRoutineModal}
            disabled={disableSelectionControls || !routineEntriesLength}
          >
            {selectedRoutineCount > 0 && (
              <span className="scheduler-chat-ui__selection__pill__badge">
                {selectedRoutineCount}
              </span>
            )}
            <span className="scheduler-chat-ui__selection__pill__label">
              {t('task.scheduler.hero.routines')}
            </span>
            <KeyboardArrowDown className="scheduler-chat-ui__selection__pill__icon" />
          </button>
          <span className="scheduler-chat-ui__selection__text">
            {t('task.scheduler.hero.for')}
          </span>
          {showDatePickerControl ? (
            <button
              type="button"
              className={clsx(
                'scheduler-chat-ui__selection__pill',
                'scheduler-chat-ui__selection__pill--outlined',
                {
                  'scheduler-chat-ui__selection__pill--active': isDatePickerOpen,
                }
              )}
              onClick={onOpenDatePicker}
              disabled={disableSelectionControls}
            >
              <span className="scheduler-chat-ui__selection__pill__label">{scheduleLabel}</span>
              <KeyboardArrowDown className="scheduler-chat-ui__selection__pill__icon" />
            </button>
          ) : (
            <span
              className={clsx(
                'scheduler-chat-ui__selection__pill',
                'scheduler-chat-ui__selection__pill--outlined'
              )}
            >
              <span className="scheduler-chat-ui__selection__pill__label">{scheduleLabel}</span>
            </span>
          )}
          <span className="scheduler-chat-ui__selection__text">.</span>
          <AddPreferenceChip
            value={customRuleInput}
            onChangeText={onCustomRuleInputChange ?? (() => undefined)}
            placeholder={t('task.scheduler.hero.anythingElse')}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default SchedulerHeroSelection;
